import { UseInfiniteQueryOptions, UseQueryOptions } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'

import { ApiPaginatedEntityResponse, dispatchCreateNotification } from 'src/action-creators'
import { useSegment } from 'src/hooks'
import {
  useEntityQuery,
  usePaginatedEntitiesQuery,
  usePatchEntityMutation,
  usePostEntityMutation,
} from 'src/queries/entity'
import { SellerTour } from 'src/types/tour'

export const TOURS_ENDPOINT = 'seller/tours'
export const TOURS_QUERY_KEY = 'tours'

export interface PaginatedQueryParams {
  limit?: number
  offset?: number
}

export interface SellerTourQueryParams {
  archived?: boolean
  buyerCompanyId?: Id
  sellerCompanyId?: Id | Id[]
}

export type GetPaginatedSellerToursQueryParams = SellerTourQueryParams & PaginatedQueryParams

export interface PaginatedSellerToursQueryArgs<TData> {
  queryParams?: GetPaginatedSellerToursQueryParams
  queryOptions?: UseInfiniteQueryOptions<
    ApiPaginatedEntityResponse<SellerTour>,
    Error,
    ApiPaginatedEntityResponse<TData>
  >
}

export const usePaginatedSellerToursQuery = <TData = SellerTour>({
  queryParams = {},
  queryOptions = {},
}: PaginatedSellerToursQueryArgs<TData> = {}) => {
  return usePaginatedEntitiesQuery<SellerTour, GetPaginatedSellerToursQueryParams, TData>({
    endpoint: TOURS_ENDPOINT,
    queryKey: TOURS_QUERY_KEY,
    queryParams,
    queryOptions: {
      cacheTime: 1000 * 60 * 60,
      staleTime: 1000 * 60 * 60,
      ...queryOptions,
      enabled: !!queryParams.sellerCompanyId && queryOptions.enabled,
    },
  })
}

export interface UseSellerTourQueryArgs<TData> {
  tourId?: Id
  queryOptions?: UseQueryOptions<SellerTour, Error, TData>
  queryParams?: SellerTourQueryParams
}

export const useSellerTourQuery = <TData = SellerTour>({
  tourId,
  queryOptions,
  queryParams,
}: UseSellerTourQueryArgs<TData>) =>
  useEntityQuery<SellerTour, {}, TData>({
    endpoint: TOURS_ENDPOINT,
    entityId: tourId,
    queryKey: TOURS_QUERY_KEY,
    queryOptions,
    queryParams,
  })

export interface PostSellerTourPayload {
  sellerCompanyId: Id
  name: string
}

export const usePostSellerTourMutation = () => {
  const dispatch = useDispatch()
  const { track } = useSegment()

  return usePostEntityMutation<PostSellerTourPayload, SellerTour>({
    endpoint: TOURS_ENDPOINT,
    queryOptions: {
      onSuccess: newTour => {
        dispatchCreateNotification(dispatch)({
          message: gettext('The tour has been successfully created'),
          type: 'positive',
        })

        track('Seller - Tour Created', {
          tourId: newTour.id,
        })
      },
    },
  })
}

export interface ArchiveSellerTourPayload {
  archived: true
  id: Id
}

export const useArchiveSellerTourMutation = () => {
  const dispatch = useDispatch()
  const { track } = useSegment()

  return usePatchEntityMutation<ArchiveSellerTourPayload, SellerTour>({
    endpoint: TOURS_ENDPOINT,
    queryOptions: {
      onSuccess: (_responseBody, payload) => {
        dispatchCreateNotification(dispatch)({
          message: gettext('The tour has been successfully archived'),
          type: 'positive',
        })

        track('Seller - Tour Archived', {
          tourId: payload.id,
        })
      },
    },
  })
}
